import '@stimulus/polyfills'
import { Application, Controller } from 'stimulus'

const app = Application.start()

app.register('refosumu--companies-index--search-panel', class extends Controller {
  static targets = [
    'prefectureInput', 'prefectureText', 'prefectureDropdown',
    'cityInput', 'cityText', 'cityDropdown', 'cityButtonsContainer',
    'citySelectedPill', 'citySelectedText',
    'partType', 'form'
  ]

  static values = {
    currentPrefecture: String,
    currentCityId: String,
    currentCityName: String
  }

  // ========================================
  // ライフサイクル
  // ========================================

  connect() {
    this.selectedPrefecture = this.currentPrefectureValue || null
    this.selectedCityId = this.currentCityIdValue || null
    this.selectedCityName = this.currentCityNameValue || null
    this.cities = []

    // ドロップダウンの外側クリックでクローズ
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.boundHandleClickOutside)

    // 初期表示時に都道府県が選択されていれば市区町村を取得
    if (this.selectedPrefecture) {
      this.loadCities(this.selectedPrefecture)
    }
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside)
  }

  handleClickOutside(event) {
    const isPrefectureInput = event.target.closest('[data-refosumu--companies-index--search-panel-target="prefectureInput"]')
    const isPrefectureDropdown = event.target.closest('[data-refosumu--companies-index--search-panel-target="prefectureDropdown"]')
    const isCityInput = event.target.closest('[data-refosumu--companies-index--search-panel-target="cityInput"]')
    const isCityDropdown = event.target.closest('[data-refosumu--companies-index--search-panel-target="cityDropdown"]')

    if (!isPrefectureInput && !isPrefectureDropdown) {
      this.closePrefectureDropdown()
    }

    if (!isCityInput && !isCityDropdown) {
      this.closeCityDropdown()
    }
  }

  // ========================================
  // 都道府県ドロップダウン
  // ========================================

  togglePrefectureDropdown(event) {
    event.stopPropagation()
    this.closeCityDropdown()

    const dropdown = this.prefectureDropdownTarget
    if (dropdown.style.display === 'none' || dropdown.style.display === '') {
      dropdown.style.display = 'block'
    } else {
      dropdown.style.display = 'none'
    }
  }

  closePrefectureDropdown() {
    if (this.hasPrefectureDropdownTarget) {
      this.prefectureDropdownTarget.style.display = 'none'
    }
  }

  async selectPrefecture(event) {
    event.preventDefault()
    const button = event.currentTarget
    const prefectureName = button.dataset.prefectureName

    this.selectedPrefecture = prefectureName

    // テキストを更新
    this.prefectureTextTarget.textContent = prefectureName
    this.prefectureInputTarget.classList.add('search-panel__selector--selected')

    // ドロップダウンを閉じる
    this.closePrefectureDropdown()

    // 市区町村をリセット
    this.selectedCityId = null
    this.selectedCityName = null
    this.hideCitySelectedPill()
    this.cityTextTarget.textContent = '選択してください'
    this.cityTextTarget.classList.remove('search-panel__selector-text--active')

    // 市区町村リストをクリアしてローディング表示
    this.cityButtonsContainerTarget.innerHTML = ''
    const loadingDiv = document.createElement('div')
    loadingDiv.className = 'search-panel__dropdown-loading'
    loadingDiv.textContent = '読み込み中...'
    this.cityButtonsContainerTarget.appendChild(loadingDiv)

    // 市区町村を取得
    await this.loadCities(prefectureName)
  }

  // ========================================
  // 市区町村ドロップダウン
  // ========================================

  toggleCityDropdown(event) {
    event.stopPropagation()

    if (!this.selectedPrefecture) {
      return
    }

    this.closePrefectureDropdown()

    const dropdown = this.cityDropdownTarget
    if (dropdown.style.display === 'none' || dropdown.style.display === '') {
      dropdown.style.display = 'block'
    } else {
      dropdown.style.display = 'none'
    }
  }

  closeCityDropdown() {
    if (this.hasCityDropdownTarget) {
      this.cityDropdownTarget.style.display = 'none'
    }
  }

  async loadCities(prefectureName) {
    if (!prefectureName) return

    try {
      const response = await fetch(`/api/prefecture/${encodeURIComponent(prefectureName)}/cities_with_major`)

      if (!response.ok) {
        this.showCityError('市区町村の取得に失敗しました')
        return
      }

      const cities = await response.json()
      this.cities = cities
      this.renderCityButtons()
    } catch (error) {
      this.showCityError('市区町村の取得中にエラーが発生しました')
    }
  }

  showCityError(message) {
    this.cityButtonsContainerTarget.innerHTML = ''
    const errorDiv = document.createElement('div')
    errorDiv.className = 'search-panel__dropdown-error'
    errorDiv.textContent = message
    this.cityButtonsContainerTarget.appendChild(errorDiv)
  }

  renderCityButtons() {
    this.cityButtonsContainerTarget.innerHTML = ''

    // 五十音順のグループ定義
    const kanaGroups = [
      { group: 'あ', kanas: ['ア', 'イ', 'ウ', 'エ', 'オ'] },
      { group: 'か', kanas: ['カ', 'キ', 'ク', 'ケ', 'コ', 'ガ', 'ギ', 'グ', 'ゲ', 'ゴ'] },
      { group: 'さ', kanas: ['サ', 'シ', 'ス', 'セ', 'ソ', 'ザ', 'ジ', 'ズ', 'ゼ', 'ゾ'] },
      { group: 'た', kanas: ['タ', 'チ', 'ツ', 'テ', 'ト', 'ダ', 'ヂ', 'ヅ', 'デ', 'ド'] },
      { group: 'な', kanas: ['ナ', 'ニ', 'ヌ', 'ネ', 'ノ'] },
      { group: 'は', kanas: ['ハ', 'ヒ', 'フ', 'ヘ', 'ホ', 'バ', 'ビ', 'ブ', 'ベ', 'ボ', 'パ', 'ピ', 'プ', 'ペ', 'ポ'] },
      { group: 'ま', kanas: ['マ', 'ミ', 'ム', 'メ', 'モ'] },
      { group: 'や', kanas: ['ヤ', 'ユ', 'ヨ'] },
      { group: 'ら', kanas: ['ラ', 'リ', 'ル', 'レ', 'ロ'] },
      { group: 'わ', kanas: ['ワ', 'ヲ', 'ン'] }
    ]

    kanaGroups.forEach(({ group, kanas }) => {
      const groupCities = this.cities.filter(city => {
        if (!city.yomi) return false
        const firstChar = city.yomi.slice(0, 1)
        return kanas.includes(firstChar)
      })

      if (groupCities.length > 0) {
        const groupTitle = document.createElement('div')
        groupTitle.className = 'search-panel__dropdown-group-title'
        groupTitle.textContent = `${group}行`
        this.cityButtonsContainerTarget.appendChild(groupTitle)

        const groupButtons = document.createElement('div')
        groupButtons.className = 'search-panel__dropdown-group-buttons'

        groupCities.forEach(city => {
          const button = document.createElement('button')
          button.className = 'search-panel__dropdown-button'
          button.type = 'button'
          button.textContent = city.name
          button.dataset.cityId = city.id
          button.dataset.cityName = city.name
          button.dataset.action = 'click->refosumu--companies-index--search-panel#selectCity'

          if (!city.enabled) {
            button.classList.add('disabled')
            button.disabled = true
          }

          groupButtons.appendChild(button)
        })

        this.cityButtonsContainerTarget.appendChild(groupButtons)
      }
    })
  }

  selectCity(event) {
    event.preventDefault()
    const button = event.currentTarget
    const cityId = button.dataset.cityId
    const cityName = button.dataset.cityName

    this.selectedCityId = cityId
    this.selectedCityName = cityName

    this.closeCityDropdown()

    // ピル型ボタンを表示
    this.showCitySelectedPill(cityName)
  }

  showCitySelectedPill(cityName) {
    // テキストを非表示
    if (this.hasCityTextTarget) {
      this.cityTextTarget.style.display = 'none'
    }

    // ピル型ボタンを表示
    if (this.hasCitySelectedPillTarget) {
      this.citySelectedPillTarget.style.display = 'flex'
    }

    // 市区町村名を設定
    if (this.hasCitySelectedTextTarget) {
      this.citySelectedTextTarget.textContent = cityName
    }

    // セレクトボックスのスタイルを更新
    if (this.hasCityInputTarget) {
      this.cityInputTarget.classList.add('search-panel__selector--selected')
      this.cityInputTarget.classList.add('search-panel__selector--has-pill')
    }
  }

  hideCitySelectedPill() {
    // ピル型ボタンを非表示
    if (this.hasCitySelectedPillTarget) {
      this.citySelectedPillTarget.style.display = 'none'
    }

    // テキストを表示
    if (this.hasCityTextTarget) {
      this.cityTextTarget.style.display = ''
    }

    // セレクトボックスのスタイルを解除
    if (this.hasCityInputTarget) {
      this.cityInputTarget.classList.remove('search-panel__selector--selected')
      this.cityInputTarget.classList.remove('search-panel__selector--has-pill')
    }
  }

  clearCity(event) {
    event.stopPropagation()
    event.preventDefault()

    // 選択状態をリセット
    this.selectedCityId = null
    this.selectedCityName = null

    // ピル型ボタンを非表示にしてテキストを表示
    this.hideCitySelectedPill()

    // テキストを「選択してください」に戻す
    if (this.hasCityTextTarget) {
      this.cityTextTarget.textContent = '選択してください'
      this.cityTextTarget.classList.remove('search-panel__selector-text--active')
    }
  }

  // ========================================
  // 検索処理
  // ========================================

  onSearchClick(event) {
    event.preventDefault()

    if (!this.selectedPrefecture) {
      alert('都道府県を選択してください')
      return
    }

    // URL生成
    let url = `/area/${encodeURIComponent(this.selectedPrefecture)}`

    if (this.selectedCityId) {
      url += `/${this.selectedCityId}`
    }

    // 工事箇所のパラメータを追加
    const checkedPartTypes = this.partTypeTargets.filter(checkbox => checkbox.checked)

    if (checkedPartTypes.length > 0) {
      const params = checkedPartTypes.map(checkbox =>
        `part_types%5B%5D=${encodeURIComponent(checkbox.value)}`
      ).join('&')
      url += `?${params}`
    }

    window.location.href = url
  }
})
